import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { FormikPinInput, PinInputField } from "../Otp";
import { Form, FormikProvider, useFormik } from "formik";
import { initialValues } from "./form";
import classNames from "classnames";
import styles from "./index.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Loader";
import BackButton from "../BackButton";
import { ReactComponent as EmailVerificationImage } from "../../assets/email_verification.svg";
import { Button } from "reactstrap";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { localstorageKeys } from "@const/store";
import { differenceInSeconds } from "date-fns";
import {
  resendVerificationLink,
  verification,
  verificationReset,
} from "@redux/verification/actions";
import { ErrorCodes } from "@const/errors";
import { ToasterService } from "@services/ToastService";
import { Trans, useTranslation } from "react-i18next";

/**
 * @param {Object} arguments
 * @param {String} arguments.password
 * @param {Boolean} arguments.isVisible
 * @param {Function} arguments.onBackClick
 * @param {Function} arguments.successCallback
 * @param {String=} arguments.title
 * @param {String=} arguments.subTitle
 * @param {Function=} arguments.sendFunction
 * @param {Function=} arguments.resendFunction
 * */

const TIMER = 60;

const VerifyEmail = (
  {
    title,
    email,
    subTitle,
    isVisible,
    onBackClick,
    sendFunction,
    resendFunction,
    successCallback,
  },
  ref,
) => {
  const { t } = useTranslation("sign-up-modal");
  const dispatch = useDispatch();
  const toasterService = useService(ToasterService);
  const storageService = useService(StorageService);
  const [errorMessage, setErrorMessage] = useState("");
  const [limitExceeded, setLimitExceeded] = useState(false);
  const onceLimitExceeded = useRef(false);

  const [seconds, setSeconds] = useState(TIMER);

  useEffect(() => {
    const sentAt = storageService.get(localstorageKeys.sentAt);
    if (!sentAt) return;

    const timer = setInterval(() => {
      const difference = differenceInSeconds(Date.now(), sentAt);
      const secondsLeft = TIMER - difference;
      setSeconds(secondsLeft < 0 ? 0 : secondsLeft);
    }, 1000);

    if (seconds === 0) {
      clearInterval(timer);
      storageService.set(localstorageKeys.sentAt, 0);
    }

    return () => {
      clearInterval(timer);
    };
  });

  const formik = useFormik({
    initialValues,
  });

  const { error, isSuccess, isLoading } = useSelector(
    ({ verificationReducer }) => verificationReducer,
  );

  const resend = () => {
    if (onceLimitExceeded.current) {
      setLimitExceeded(true);
      return;
    }

    formik.resetForm();
    if (resendFunction) {
      resendFunction();
    } else {
      dispatch(resendVerificationLink({ email }));
    }
    setSeconds(TIMER);
    storageService.set(localstorageKeys.sentAt, Date.now());
  };

  const sendLastCode = () => {
    setLimitExceeded(false);
    dispatch(verificationReset());
    setErrorMessage("");
    setSeconds(0);
    storageService.set(localstorageKeys.sentAt, 0);
  };

  useEffect(() => {
    if (formik.values.emailVerificationCode?.length !== 6 || isLoading) return;
    if (sendFunction) {
      sendFunction(formik.values.emailVerificationCode);
    } else {
      dispatch(
        verification({
          email,
          code: formik.values.emailVerificationCode,
        }),
      );
    }
  }, [formik.values]);

  useEffect(() => {
    if (!isSuccess) return;

    dispatch(verificationReset());
    successCallback(formik.values.emailVerificationCode);
  }, [isSuccess]);

  useEffect(() => {
    if (!error) {
      setErrorMessage("");
      return;
    }

    if (error?.code === ErrorCodes.RESEND_CODE_60_SEC) {
      toasterService.error(error.message);
      return;
    }

    if (error?.code === ErrorCodes.RESEND_LIMIT_EXCEED) {
      setLimitExceeded(true);
      onceLimitExceeded.current = true;
      return;
    }

    setErrorMessage(error.message);
  }, [error]);

  useImperativeHandle(ref, () => ({
    resetLimit: () => {
      setLimitExceeded(false);
      onceLimitExceeded.current = false;
    },
    resetSeconds: () => {
      setSeconds(TIMER);
    },
  }));

  return (
    <div
      style={{
        display: isVisible ? "block" : "none",
      }}
    >
      {isLoading && (
        <div
          className={classNames(
            "position-absolute d-flex justify-content-center align-items-center",
            styles.codeSpinner,
          )}
        >
          <Loader className="spinning" />
        </div>
      )}

      {onBackClick && <BackButton onClick={onBackClick} />}

      <div className="d-flex flex-column">
        <span
          className={classNames("text-center h3 text-primary", styles.title)}
        >
          {title ?? t("VERIFICATION_TITLE")}
        </span>

        {!limitExceeded ? (
          <>
            <span className="text-body-1 text-center text-primary">
              {subTitle ?? (
                <Trans
                  t={t}
                  components={{
                    span: (
                      <span className="text-medium text-body-1 text-primary">
                        {email}
                      </span>
                    ),
                  }}
                  i18nKey="VERIFICATION_BODY"
                />
              )}
            </span>
            <span className="text-regular text-center text-body-2 text-tertiary">
              {t("DID_NOT_RECEIVE_TIP")}
            </span>
            <FormikProvider value={formik}>
              <Form className="d-flex flex-column align-items-center">
                <FormikPinInput
                  name="emailVerificationCode"
                  customError={error?.message}
                  className="mb-1 mt-4 position-relative"
                >
                  <PinInputField customError={error?.message} />
                  <PinInputField customError={error?.message} />
                  <PinInputField customError={error?.message} />
                  <PinInputField customError={error?.message} />
                  <PinInputField customError={error?.message} />
                  <PinInputField customError={error?.message} />
                  {errorMessage ? (
                    <span className={classNames("caption", styles.error)}>
                      {errorMessage}
                    </span>
                  ) : null}
                </FormikPinInput>
              </Form>
            </FormikProvider>
            <div className="d-flex align-items-center justify-content-center">
              <span
                className={classNames(
                  "text-regular text-body-2 text-tertiary d-block",
                  styles.resendQuestion,
                )}
              >
                {t("RESEND_QUESTION")}
              </span>
              {seconds ? (
                <span className="text-primary">
                  {t("RESEND_BODY", { sec: seconds })}
                </span>
              ) : (
                <span
                  onClick={resend}
                  className={classNames(
                    "text-medium cursor-pointer",
                    styles.resend,
                  )}
                >
                  {t("RESEND")}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center">
            <EmailVerificationImage />
            <span className="text-body-1 text-primary">{t("CAN_RESEND")}</span>
            <span className="text-body-1 text-primary">{t("TRY_AGAIN")}</span>
            <Button
              className={styles.thanksButton}
              color="primary"
              onClick={sendLastCode}
            >
              <span className="text-black">{t("THANKS")}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default forwardRef(VerifyEmail);
